import React, { useState } from "react";
import { ChevronDown, ChevronUp, Trash2, Plus } from "react-feather";
import { C } from "../../reporting/reportingStyles";
import BilingualField from "../BilingualField";
import { LIMITS, CALLOUT_TONES, CONTACT_FIELDS, newBlockId } from "../../../constants/merchantPages";
import { inputStyle, labelStyle, hintStyle, iconBtn, chip, linkBtn } from "../pageStyles";

/**
 * One editor per block type. Each is controlled: `block` in, `onChange(patch)` out, where the
 * patch is merged into the block by SectionsEditor.
 */

const pair = (en = null, ar = null) => ({ en, ar });

const swap = (list, i, dir) => {
  const to = i + dir;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[i], next[to]] = [next[to], next[i]];
  return next;
};

const subBox = {
  border: `1px solid ${C.borderLight}`,
  borderRadius: 8,
  padding: 12,
  marginBottom: 10,
  background: C.bg,
};

export const RichTextBlockEditor = ({ block, onChange, disabled }) => (
  <BilingualField
    label="Text"
    kind="rich"
    value={block.body}
    onChange={(body) => onChange({ body })}
    disabled={disabled}
    requiredEn
    testId={`rich-${block.id}`}
  />
);

export const AccordionBlockEditor = ({ block, onChange, disabled }) => {
  const items = block.items || [];
  const setItems = (next) => onChange({ items: next });
  const updateItem = (i, patch) => setItems(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems([...items, { id: newBlockId(), title: pair(), body: pair() }]);

  return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.textMed, marginBottom: 10 }}>
        <input
          type="checkbox"
          checked={Boolean(block.allowMultiple)}
          disabled={disabled}
          onChange={(e) => onChange({ allowMultiple: e.target.checked })}
        />
        Allow several items open at once
      </label>

      {items.map((item, i) => (
        <div key={item.id} style={subBox}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <strong style={{ fontSize: 12, color: C.text, flex: 1 }}>Item {i + 1}</strong>
            {!disabled && (
              <>
                <button type="button" style={iconBtn} disabled={i === 0} onClick={() => setItems(swap(items, i, -1))} title="Move up"><ChevronUp size={14} /></button>
                <button type="button" style={iconBtn} disabled={i === items.length - 1} onClick={() => setItems(swap(items, i, 1))} title="Move down"><ChevronDown size={14} /></button>
                <button type="button" style={iconBtn} disabled={items.length === 1} onClick={() => setItems(items.filter((_, j) => j !== i))} title="Remove item"><Trash2 size={14} /></button>
              </>
            )}
          </div>
          <BilingualField
            label="Question / title"
            value={item.title}
            onChange={(title) => updateItem(i, { title })}
            maxLength={LIMITS.accordionTitle}
            disabled={disabled}
            requiredEn
          />
          <BilingualField
            label="Answer"
            kind="rich"
            value={item.body}
            onChange={(body) => updateItem(i, { body })}
            disabled={disabled}
            requiredEn
          />
        </div>
      ))}

      {!disabled && (
        <button type="button" style={linkBtn} disabled={items.length >= LIMITS.maxAccordionItems} onClick={addItem}>
          <Plus size={12} /> Add item {items.length >= LIMITS.maxAccordionItems ? `(limit ${LIMITS.maxAccordionItems})` : ""}
        </button>
      )}
    </div>
  );
};

export const TableBlockEditor = ({ block, onChange, disabled }) => {
  const [lang, setLang] = useState("en");
  const columns = block.columns || [];
  const rows = block.rows || [];
  const dir = lang === "ar" ? "rtl" : "ltr";

  const setCell = (r, c, text) =>
    onChange({ rows: rows.map((row, i) => (i !== r ? row : row.map((cell, j) => (j !== c ? cell : { ...cell, [lang]: text || null })))) });
  const setColumn = (c, text) => onChange({ columns: columns.map((col, j) => (j !== c ? col : { ...col, [lang]: text || null })) });
  const addColumn = () => onChange({ columns: [...columns, pair(`Column ${columns.length + 1}`)], rows: rows.map((row) => [...row, pair()]) });
  const removeColumn = (c) => onChange({ columns: columns.filter((_, j) => j !== c), rows: rows.map((row) => row.filter((_, j) => j !== c)) });
  const addRow = () => onChange({ rows: [...rows, columns.map(() => pair())] });
  const removeRow = (r) => onChange({ rows: rows.filter((_, i) => i !== r) });

  const cellInput = { ...inputStyle, padding: "6px 8px", fontSize: 12, minWidth: 120 };

  return (
    <div>
      <BilingualField label="Caption (optional)" value={block.caption} onChange={(caption) => onChange({ caption })} maxLength={LIMITS.tableCaption} disabled={disabled} />

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <span style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Cells — plain text</span>
        {["en", "ar"].map((l) => (
          <button key={l} type="button" style={chip(lang === l)} onClick={() => setLang(l)} aria-pressed={lang === l}>
            {l === "en" ? "English" : "العربية"}
          </button>
        ))}
      </div>

      <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }} dir={dir}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {columns.map((col, c) => (
                <th key={c} style={{ padding: 6, background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input
                      aria-label={`Column ${c + 1} heading`}
                      style={{ ...cellInput, fontWeight: 600 }}
                      value={col[lang] || ""}
                      maxLength={LIMITS.tableCell}
                      disabled={disabled}
                      placeholder={lang === "ar" ? (col.en || "") : "Heading"}
                      onChange={(e) => setColumn(c, e.target.value)}
                    />
                    {!disabled && columns.length > 1 && (
                      <button type="button" style={iconBtn} onClick={() => removeColumn(c)} title="Remove column"><Trash2 size={12} /></button>
                    )}
                  </div>
                </th>
              ))}
              <th style={{ width: 36, background: C.bg, borderBottom: `1px solid ${C.border}` }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c} style={{ padding: 6, borderBottom: `1px solid ${C.borderLight}` }}>
                    <input
                      aria-label={`Row ${r + 1} cell ${c + 1}`}
                      style={cellInput}
                      value={cell[lang] || ""}
                      maxLength={LIMITS.tableCell}
                      disabled={disabled}
                      placeholder={lang === "ar" ? (cell.en || "") : ""}
                      onChange={(e) => setCell(r, c, e.target.value)}
                    />
                  </td>
                ))}
                <td style={{ padding: 6, borderBottom: `1px solid ${C.borderLight}` }}>
                  {!disabled && <button type="button" style={iconBtn} onClick={() => removeRow(r)} title="Remove row"><Trash2 size={12} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!disabled && (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button type="button" style={linkBtn} disabled={rows.length >= LIMITS.maxTableRows} onClick={addRow}><Plus size={12} /> Add row</button>
          <button type="button" style={linkBtn} disabled={columns.length >= LIMITS.maxTableColumns} onClick={addColumn}><Plus size={12} /> Add column</button>
        </div>
      )}
      <div style={hintStyle}>Empty Arabic cells show the English text. Up to {LIMITS.maxTableColumns} columns and {LIMITS.maxTableRows} rows.</div>
    </div>
  );
};

export const CalloutBlockEditor = ({ block, onChange, disabled }) => (
  <div>
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>Tone</span>
      <div style={{ display: "flex", gap: 8 }}>
        {CALLOUT_TONES.map((t) => (
          <button
            key={t.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange({ tone: t.value })}
            aria-pressed={block.tone === t.value}
            style={{
              ...chip(block.tone === t.value),
              borderColor: block.tone === t.value ? t.color : C.border,
              background: block.tone === t.value ? t.bg : C.white,
              color: block.tone === t.value ? t.color : C.textMed,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
    <BilingualField label="Title (optional)" value={block.title} onChange={(title) => onChange({ title })} maxLength={LIMITS.calloutTitle} disabled={disabled} />
    <BilingualField label="Text" kind="rich" value={block.body} onChange={(body) => onChange({ body })} disabled={disabled} requiredEn />
  </div>
);

export const ContactBlockEditor = ({ block, onChange, disabled }) => {
  const fields = block.fields || {};
  const none = !Object.values(fields).some(Boolean);
  return (
    <div>
      <span style={labelStyle}>Show</span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
        {CONTACT_FIELDS.map((f) => (
          <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text }} title={`From: ${f.source}`}>
            <input
              type="checkbox"
              checked={Boolean(fields[f.key])}
              disabled={disabled}
              onChange={(e) => onChange({ fields: { ...fields, [f.key]: e.target.checked } })}
            />
            {f.label}
          </label>
        ))}
      </div>
      {none && <div style={{ ...hintStyle, color: "#c0392b" }}>Choose at least one.</div>}
      <div style={hintStyle}>Values come from Company Configuration when the page is shown, so they stay current. Empty ones are hidden.</div>
    </div>
  );
};

export const DividerBlockEditor = () => (
  <div style={hintStyle}>A horizontal line. Give it a heading to start a new part of the page.</div>
);
